import {
  useEffect,
  useCallback,
  type ComponentType,
  useReducer,
  useRef,
  Suspense,
} from 'react'

import { SplashOverlay } from '@src/layouts/ui'

type UseLazyComponentProps<P> = {
  Component: ComponentType<P>
  isOpenInitial: boolean
  /** should be 100vw * 100vh and z-index over 1201 */
  LoadingLayout?: () => JSX.Element
}

/**
 * ## Custom hook to lazily load a component with an optional loading layout based on the condition.
 * - If initial display is required, it shows the `LoadingLayout` until the component is fully loaded.
 * - If initial display is not required, it asynchronously loads the component to improve initial render performance.
 * ### Motivation
 * - If the component is not required to be displayed immediately, we do not have to show the loading layout. However, if we use `dynamic` from `next/dynamic`, we are forced to show the loading layout until the component is fully loaded.
 * - This is useful when rendring of the target component is controlled by props, not by {condition && <Component />}.
 *
 * **Note: This hook is not necessary if the component's necessity is known beforehand. If the component is needed on initial render, it should be directly imported to avoid the overhead of asynchronous loading. If the component is not needed on initial render, use `dynamic` from `next/dynamic` instead.**
 * @param Component - The component to be lazily loaded.
 * @example
 * ```tsx
 * import {lazy} from 'react'
 * import { useLazyComponent } from '@src/hooks'
 *
 * const LazyComponent = useLazyComponent({
 *  Component: lazy(() => import('path/to/component')),
 *  isOpenInitial: condition,
 *  LoadingLayout: () => <SplashOverlay fixed />,
 * })
 * ```
 */
export const useLazyComponent = <P,>({
  Component,
  isOpenInitial,
  LoadingLayout = () => <SplashOverlay fixed />,
}: UseLazyComponentProps<P>) => {
  const initialOpenRef = useRef(isOpenInitial)
  const [isLoaded, setIsLoaded] = useReducer(() => true, initialOpenRef.current)

  /**
   * ### Call this function when the LazyComponent is loaded.
   * - Before calling this function, the LazyComponent is still being fetched.
   * - After calling this function, we are certain that the LazyComponent is loaded and hide the loading layout.
   */
  const handleLoad = useCallback(() => setIsLoaded(), [])

  useEffect(() => {
    if (!initialOpenRef.current) {
      // Load the component after initial render to not block the initial render
      const timer = setTimeout(() => {
        handleLoad()
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [handleLoad])

  return useCallback(
    <L extends JSX.IntrinsicAttributes>(props: L & P) => {
      if (!initialOpenRef.current) {
        return isLoaded ? <Component {...props} /> : null
      }
      return (
        <>
          <Suspense fallback={<LoadingLayout />}>
            <Component {...props} />
          </Suspense>
        </>
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoaded]
  )
}
